export enum EDatasourceType {
  ManagedDb = 0,
  CustomDb = 1,
  RestApi = 2,
  CSharpFunctions = 3,
  SystemCSFunctions = 4,
  SystemJSFunctions = 5,
  CustomAuthenticationProvider = 6,
  CustomAuthorizationProvider = 7,
  MsSql = 8,
  RestApiWOSwagger = 9,
  CustomVerificationCodeSenderProvider = 10,
  BleService = 11,
  AIActions = 12,
  Oracle = 13,
  ManagedDbOracle = 14,
  Mqtt = 15,
  Blockly = 16,
  ManagedDbPostgre = 17,
  Postgre = 18,
  AIAgent = 20,
  VisionAction = 21,
  // Retired: existing datasources still carry it, but it can no longer be created and the backend
  // never compiles or emits it.
  CustomConnectionStringProvider = 22,
  OData = 23,
  SAP = 24,
  JavaScriptAction = 25,
  PythonAction = 26,
  JavaAction = 27,
  GoAction = 28,
  RustAction = 29,
  CustomExternalTokenValidatorProvider = 40,
  CustomMailProvider = 41
}

export enum EStatementTypes {
  None = -1,
  CustomExecuteReader = 0,
  CustomExecuteNonQuery = 1,
  Save = 2,
  Delete = 3,
  Select = 5
}

export enum EActionCategory {
  Other = 0,
  Advanced = 1,
  Custom = 2,
  Device = 3,
  Export = 4,
  Geolocation = 5,
  Navigation = 6,
  Notification = 7,
  UIControl = 8,
  Arithmetic = 9,
  Authorization = 10,
  LocalStorage = 11,
  MultiLanguage = 12,
  Condition = 13,
  ProcessAutomation = 14,
  Trigger = 15,
  Payment = 16,
  Invers = 17,
  ManagedDb = 18,
  StringOperations = 19,
  ArrayOperations = 20,
  ProcessAdministration = 21,
  RoboticProcessAutomation = 22,
  OfflineDatabase = 23,
  PivotTable = 24,
  Mqtt = 25,
  VectorSearch = 26,
  Verification = 27,
  Bag = 28,
  EAV = 29,
  Cache = 30,
  Vision = 31,
  BraveSearch = 32,
  Dropbox = 33,
  Github = 34,
  Google = 35,
  Jira = 36,
  Slack = 37,
  Youtube = 38,
  Airbnb = 39,
  Trello = 40,
  Office365 = 41,
  DocumentManagement = 42,
  SearchEngine = 43
}

export enum EActionSubCategory {
  None = 0,
  Entity = 1,
  Attribute = 2,
  EntityValue = 3,
  EntityAttribute = 4,
  EntityAttributeValue = 5,
  Category = 6,
  Localization = 7,
  EnumItem = 8,
  DynamicForm = 9,
  // Document Management
  Document = 10,
  Folder = 11,
  Search = 12,
  Permission = 13,
  Share = 14,
  Archive = 15,
  Audit = 17,
  // Template & Version Management
  Template = 19,
  Version = 20,
  Tag = 21,
  Relation = 22,
  // Full-Text Search Engine (OpenSearch / ElasticSearch / Meilisearch)
  FullTextSearch = 25,
  Checkout = 26,
  Draft = 27,
  Dashboard = 29,
  DocumentProperties = 30,
  Trash = 31,
  Temp = 32,
  Comment = 18,
  BulkOperation = 23,
  Admin = 24,
  FormatOutput = 33
}

export const DATASOURCE_TYPE_DISPLAY_NAMES: Record<number, string> = {
  [EDatasourceType.ManagedDb]: "Managed DB",
  [EDatasourceType.CustomDb]: "Custom DB",
  [EDatasourceType.RestApi]: "REST API",
  [EDatasourceType.CSharpFunctions]: "C# Functions",
  [EDatasourceType.SystemCSFunctions]: "System C# Functions",
  [EDatasourceType.SystemJSFunctions]: "System JS Functions",
  [EDatasourceType.CustomAuthenticationProvider]: "Auth Provider",
  [EDatasourceType.CustomAuthorizationProvider]: "Authorization Provider",
  [EDatasourceType.MsSql]: "MS SQL",
  [EDatasourceType.RestApiWOSwagger]: "REST API (w/o Swagger)",
  [EDatasourceType.CustomVerificationCodeSenderProvider]: "Verification Code Provider",
  [EDatasourceType.BleService]: "BLE Service",
  [EDatasourceType.AIActions]: "AI Actions",
  [EDatasourceType.Oracle]: "Oracle",
  [EDatasourceType.ManagedDbOracle]: "Managed DB (Oracle)",
  [EDatasourceType.Mqtt]: "MQTT",
  [EDatasourceType.Blockly]: "Blockly",
  [EDatasourceType.ManagedDbPostgre]: "Managed DB (PostgreSQL)",
  [EDatasourceType.Postgre]: "PostgreSQL",
  [EDatasourceType.AIAgent]: "AI Agent",
  [EDatasourceType.VisionAction]: "Vision Action",
  [EDatasourceType.CustomConnectionStringProvider]: "Connection String Provider",
  [EDatasourceType.OData]: "OData",
  [EDatasourceType.SAP]: "SAP",
  [EDatasourceType.JavaScriptAction]: "TypeScript Functions",
  [EDatasourceType.PythonAction]: "Python Functions",
  [EDatasourceType.JavaAction]: "Java Functions",
  [EDatasourceType.GoAction]: "Go Functions",
  [EDatasourceType.RustAction]: "Rust Functions",
  [EDatasourceType.CustomExternalTokenValidatorProvider]: "External Token Validator",
  [EDatasourceType.CustomMailProvider]: "Mail Provider"
};

export const STATEMENT_TYPE_DISPLAY_NAMES: Record<number, string> = {
  [EStatementTypes.CustomExecuteReader]: "Execute Reader",
  [EStatementTypes.CustomExecuteNonQuery]: "Execute Non-Query",
  [EStatementTypes.Save]: "Save",
  [EStatementTypes.Delete]: "Delete",
  [EStatementTypes.Select]: "Select"
};

export const DB_DATASOURCE_TYPES = new Set([
  EDatasourceType.ManagedDb,
  EDatasourceType.CustomDb,
  EDatasourceType.MsSql,
  EDatasourceType.Oracle,
  EDatasourceType.ManagedDbOracle,
  EDatasourceType.ManagedDbPostgre,
  EDatasourceType.Postgre
]);

export const CSHARP_DATASOURCE_TYPES = new Set([EDatasourceType.CSharpFunctions, EDatasourceType.SystemCSFunctions]);

export const REST_DATASOURCE_TYPES = new Set([
  EDatasourceType.RestApi,
  EDatasourceType.RestApiWOSwagger,
  EDatasourceType.OData
]);

/**
 * Backend languages authored as a multi-file project tree (vs. the single-`code`-string
 * JavaScriptAction/CSharpFunctions datasources). These execute server-side in out-of-process,
 * version-pinned, TTL-recycled worker pools. See docs/multi-language-backend-datasources-spec.md.
 */
export const PROJECT_DATASOURCE_TYPES = new Set([
  EDatasourceType.PythonAction,
  EDatasourceType.JavaAction,
  EDatasourceType.GoAction,
  EDatasourceType.RustAction
]);

/**
 * Datasources authored as a single inline `code` string whose actions are auto-extracted from the code —
 * there is no per-action editing UI, and "edit" opens the code editor. JavaScript/TypeScript and Python
 * behave identically in the designer, so UI that special-cases JavaScriptAction should use this predicate.
 */
export const INLINE_CODE_DATASOURCE_TYPES = new Set([
  EDatasourceType.JavaScriptAction,
  EDatasourceType.PythonAction,
  EDatasourceType.JavaAction,
  EDatasourceType.GoAction,
  EDatasourceType.RustAction
]);

export const isInlineCodeDatasourceType = (type: EDatasourceType | undefined): boolean =>
  type !== undefined && INLINE_CODE_DATASOURCE_TYPES.has(type);
